import { useEffect, useMemo } from 'react';
import { createTheme, GlobalStyles, ThemeProvider } from '@mui/material';

import { THEME_KEY, readMode, useAppearance } from './appearance';

import baseTheme from './index';

import type { ReactNode } from 'react';

export default function AppearanceProvider({ children }: { children: ReactNode }) {
    const mode = useAppearance((s) => s.mode);
    const dark = mode === 'dark';
    const colors = useMemo(
        () => ({
            text: dark ? '#f7f8fb' : '#000000',
            muted: dark ? '#a3adbd' : '#5c7088',
            surface: dark ? '#0e1117' : '#ffffff',
            canvas: dark ? '#080b10' : '#eff3f7',
            border: dark ? '#293344' : '#e3ecf3',
            soft: dark ? '#152340' : '#e7efff',
            ink: dark ? '#79a7ff' : '#0044ff',
            accent: '#0044ff',
            contrast: '#ffffff',
            hover: dark ? '#1a2230' : '#e3ecf3',
            disabled: dark ? '#697180' : '#9fa9bc',
        }),
        [dark]
    );
    const theme = useMemo(
        () =>
            createTheme(baseTheme, {
                palette: {
                    mode,
                    primary: { main: dark ? colors.accent : '#000000', contrastText: dark ? colors.contrast : '#fff' },
                    secondary: { main: colors.muted },
                    info: { main: colors.ink, contrastText: dark ? colors.contrast : '#fff' },
                    background: { default: colors.surface, paper: colors.canvas },
                    text: { primary: colors.text, secondary: colors.muted, disabled: colors.disabled },
                    divider: colors.border,
                    action: {
                        active: colors.muted,
                        hover: colors.hover,
                        selected: colors.soft,
                        disabled: colors.disabled,
                        disabledBackground: colors.hover,
                    },
                },
                components: {
                    MuiTooltip: {
                        styleOverrides: {
                            tooltip: { backgroundColor: colors.text, color: colors.surface, fontSize: 12 },
                            arrow: { color: colors.text },
                        },
                    },
                    MuiTab: {
                        styleOverrides: {
                            root: { '&.Mui-selected': { color: dark ? colors.ink : '#000000' } },
                        },
                    },
                    MuiTabs: {
                        styleOverrides: { indicator: { backgroundColor: dark ? colors.ink : '#000000' } },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                '&.Mui-disabled': { color: colors.disabled, borderColor: colors.border },
                                '@media (max-width: 899px)': { minHeight: 40 },
                            },
                            contained: {
                                color: dark ? colors.contrast : '#fff',
                                '&.Mui-disabled': { color: colors.disabled, background: colors.hover },
                            },
                            containedInfo: { backgroundColor: colors.accent, color: colors.contrast },
                            outlined: {
                                color: colors.text,
                                border: `1px solid ${colors.border}`,
                                backgroundColor: 'transparent',
                                '&:hover': { backgroundColor: colors.hover, borderColor: colors.muted },
                            },
                            text: {
                                color: colors.text,
                                '&:hover': { color: colors.text, backgroundColor: colors.hover },
                            },
                        },
                    },
                    MuiCard: { styleOverrides: { root: { backgroundColor: colors.surface } } },
                    MuiMenu: { styleOverrides: { paper: { backgroundColor: colors.surface } } },
                    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none', color: colors.text } } },
                    MuiInputBase: {
                        styleOverrides: {
                            root: {
                                color: colors.text,
                                '&.Mui-focused': { color: colors.text },
                                '&.Mui-disabled': {
                                    backgroundColor: colors.hover,
                                    color: colors.disabled,
                                    WebkitTextFillColor: colors.disabled,
                                },
                                '&.MuiInputBase-multiline': { height: 'auto' },
                            },
                            input: {
                                '&::placeholder': { color: colors.muted, opacity: 1 },
                                '&.Mui-disabled': { WebkitTextFillColor: colors.disabled },
                            },
                        },
                    },
                    MuiInputLabel: {
                        styleOverrides: { root: { color: colors.muted, '&.Mui-focused': { color: colors.ink } } },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.muted },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.ink },
                                '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                            },
                            notchedOutline: { borderColor: colors.border },
                            input: {
                                '&.Mui-disabled': {
                                    backgroundColor: colors.hover,
                                    color: colors.disabled,
                                    WebkitTextFillColor: colors.disabled,
                                },
                            },
                        },
                    },
                    MuiCheckbox: {
                        styleOverrides: {
                            colorPrimary: { color: colors.muted },
                            root: { '&.Mui-checked:not(.Mui-disabled)': { color: colors.ink } },
                        },
                    },
                    MuiRadio: {
                        styleOverrides: {
                            root: { color: colors.muted, '&.Mui-checked:not(.Mui-disabled)': { color: colors.ink } },
                        },
                    },
                    MuiSwitch: {
                        styleOverrides: {
                            switchBase: {
                                '&.Mui-checked:not(.Mui-disabled)': { color: colors.ink },
                                '&.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent },
                            },
                        },
                    },
                    MuiSlider: { styleOverrides: { colorPrimary: { color: colors.ink } } },
                    MuiLinearProgress: {
                        styleOverrides: {
                            root: { backgroundColor: colors.border },
                            barColorPrimary: { background: colors.accent },
                            barColorInfo: { background: colors.accent },
                        },
                    },
                    MuiAlert: {
                        styleOverrides: {
                            standardError: {
                                color: dark ? '#ffb4ab' : '#8f2828',
                                backgroundColor: dark ? '#382320' : '#fff1f0',
                            },
                            standardInfo: { color: colors.text, backgroundColor: colors.soft },
                        },
                    },
                    MuiChip: {
                        styleOverrides: {
                            filled: { '&.MuiChip-filledDefault': { backgroundColor: colors.hover } },
                            colorInfo: { backgroundColor: colors.soft, color: colors.ink },
                            colorSuccess: {
                                backgroundColor: dark ? '#1e3025' : '#f2fbf6',
                                color: dark ? '#91d6ac' : '#16794c',
                            },
                            colorWarning: {
                                backgroundColor: dark ? '#332b18' : '#fff7de',
                                color: dark ? '#f0ce78' : '#815b09',
                            },
                            colorError: {
                                backgroundColor: dark ? '#382320' : '#fff1f3',
                                color: dark ? '#ffb4ab' : '#b42336',
                            },
                        },
                    },
                },
            }),
        [mode, dark, colors]
    );
    useEffect(() => {
        document.documentElement.dataset.theme = mode;
        document.documentElement.dataset.accent = 'blue';
    }, [mode]);
    useEffect(() => {
        const sync = (e: StorageEvent) => {
            if (e.key === THEME_KEY || e.key === null) useAppearance.setState({ mode: readMode() });
        };
        window.addEventListener('storage', sync);
        return () => window.removeEventListener('storage', sync);
    }, []);
    return (
        <ThemeProvider theme={theme}>
            <GlobalStyles
                styles={{
                    ':root': {
                        colorScheme: mode,
                        '--lm-text': colors.text,
                        '--lm-muted': colors.muted,
                        '--lm-surface': colors.surface,
                        '--lm-canvas': colors.canvas,
                        '--lm-border': colors.border,
                        '--lm-hover': colors.hover,
                        '--lm-disabled': colors.disabled,
                        '--lm-solid': dark ? colors.accent : '#000000',
                        '--lm-scrollbar': dark ? '#4a5669' : '#94a3b8',
                        '--lm-accent-border': dark ? '#35558b' : '#c7d7fe',
                        '--lm-success-bg': dark ? '#1e3025' : '#f2fbf6',
                        '--lm-success-ink': dark ? '#91d6ac' : '#16794c',
                        '--lm-success-fill': '#16794c',
                        '--lm-success-border': dark ? '#365b44' : '#bfe8d1',
                        '--lm-danger-bg': dark ? '#382320' : '#fff1f3',
                        '--lm-danger-ink': dark ? '#ffb4ab' : '#b42336',
                        '--lm-danger-border': dark ? '#724139' : '#f3c6cd',
                        '--lm-warning-bg': dark ? '#332b18' : '#fff7de',
                        '--lm-warning-ink': dark ? '#f0ce78' : '#815b09',
                        '--lm-accent': colors.accent,
                        '--lm-accent-fill': colors.accent,
                        '--lm-accent-ink': colors.ink,
                        '--lm-accent-soft': colors.soft,
                        '--lm-accent-contrast': colors.contrast,
                        '--intent-accent': colors.accent,
                        '--intent-accent-ink': colors.ink,
                        '--intent-accent-contrast': colors.contrast,
                    },
                    body: {
                        background: colors.canvas,
                        color: colors.text,
                        fontFamily: baseTheme.typography.fontFamily,
                    },
                    'nav[aria-label="Основное меню"] [aria-current="page"]': {
                        background: colors.soft,
                        color: colors.ink,
                    },
                    ':focus-visible': { outlineColor: colors.ink },
                }}
            />
            {children}
        </ThemeProvider>
    );
}
